import React, { useEffect, useCallback } from 'react';

import useSubmitClaim from './useSubmitClaim';
import CreateClaim from '../../parcel-finder/claims/CreateClaim';
import useSearchShipment from './useSearchShipment';
import { trackEvent } from '../../../utils/googleAnalytics';

function CreateClaimContainer({ shipment, draftId, entryPoint, onClose, onClaimSubmitted }) {
  const {
    claim,
    isClaimSubmitting,
    isClaimDraftSaving,
    isClaimsAutomated,
    changeClaim,
    resetClaim,
    submitClaim,
    saveClaimDraft,
  } = useSubmitClaim({
    shipment,
    draftId,
    entryPoint,
  });

  const { isSearching, nonClaimableMessage, resetShipmentSearch, loadShipmentByTrackingId } = useSearchShipment({
    claim,
    changeClaim,
    resetClaim,
  });

  const trackClaimAction = useCallback((action) => trackEvent({ category: entryPoint, action }), [entryPoint]);

  useEffect(() => {
    trackClaimAction('Claim initiated');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCloseHandler = useCallback(
    (step) => {
      trackClaimAction(`Claim exit: ${step}`);
      onClose();
    },
    [onClose, trackClaimAction]
  );

  const onSubmitHandler = useCallback(() => {
    submitClaim()
      .then(() => {
        trackClaimAction('Claim submitted');
        onClaimSubmitted();
        onClose();
      })
      .catch(() => trackClaimAction('Claim submission failed'));
  }, [submitClaim, onClose, onClaimSubmitted, trackClaimAction]);

  const onSaveDraftHandler = useCallback(() => {
    saveClaimDraft()
      .then(() => {
        trackClaimAction('Claim draft saved');
        onClaimSubmitted();
        onClose();
      })
      .catch(() => trackClaimAction('Claim draft saving failed'));
  }, [saveClaimDraft, onClose, onClaimSubmitted, trackClaimAction]);

  return (
    <CreateClaim
      claim={claim}
      resetClaim={resetClaim}
      onChange={changeClaim}
      onLoadShipmentByTrackingId={loadShipmentByTrackingId}
      onResetShipmentSearch={resetShipmentSearch}
      onClose={onCloseHandler}
      onSubmit={onSubmitHandler}
      onSaveDraft={onSaveDraftHandler}
      isSubmitting={isClaimSubmitting || isSearching}
      isClaimDraftSaving={isClaimDraftSaving}
      isDisabled={isSearching}
      nonClaimableMessage={nonClaimableMessage}
      isClaimsAutomated={isClaimsAutomated}
    />
  );
}

export default React.memo(CreateClaimContainer);
